import { S3Client, PutObjectCommand, PutObjectCommandInput, GetObjectCommand } from '@aws-sdk/client-s3';
import { Readable, PassThrough } from 'stream';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  region?: string;
}

export interface UploadParams {
  data: Buffer | Uint8Array | string | Readable;
  bucket: string;
  key: string;
  contentType?: string;
  acl?: string;
  metadata?: Record<string, string>;
  cacheControl?: string;
  expires?: Date;
  contentDisposition?: string;
}

export class AwsBucket {
  private s3Client: S3Client;

  constructor(credentials: AwsCredentials) {
    this.s3Client = new S3Client({
      region: credentials?.region ?? 'sa-east-1',
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
      },
    });
  }

  private createS3Params(params: UploadParams | Omit<UploadParams, 'data'>, body?: any): PutObjectCommandInput {
    return {
      Bucket: params.bucket,
      Key: params.key,
      Body: body || (params as UploadParams).data,
      ContentType: params.contentType,
      ACL: params.acl as any,
      Metadata: params.metadata,
      CacheControl: params.cacheControl,
      Expires: params.expires,
      ContentDisposition: params?.contentDisposition,
    };
  }

  async upload(params: UploadParams) {
    const s3Params = this.createS3Params(params);
    const command = new PutObjectCommand(s3Params);
    return await this.s3Client.send(command);
  }

  async getObjectWebStream(params: { bucket: string; key: string }) {
    const command = new GetObjectCommand({
      Bucket: params.bucket,
      Key: params.key,
    });
    const response = await this.s3Client.send(command);
    return response.Body?.transformToWebStream();
  }

  uploadStream(params: Omit<UploadParams, 'data'>): { writeStream: PassThrough; uploadPromise: Promise<any> } {
    const passThrough = new PassThrough();
    const s3Params = this.createS3Params(params, passThrough);
    const command = new PutObjectCommand(s3Params);
    const uploadPromise = this.s3Client.send(command);

    return { writeStream: passThrough, uploadPromise };
  }

  getS3Client(): S3Client {
    return this.s3Client;
  }
}

export const createAwsBucket = (credentials: AwsCredentials): AwsBucket => {
  return new AwsBucket(credentials);
};
